import { request } from "../client"

export type OrganizationPerformancePeriod = {
  days: 7 | 30 | 90
  planned_work_orders: number
  planning_complete: boolean
  billed_credits: number
  credits_complete: boolean
  usage_revenue_cents: number
  estimated_base_fee_cents: number | null
  direct_cost_cents: number | null
  cost_breakdown_cents: Record<string, number>
  estimated_profit_cents: number | null
  missing_data: string[]
}

export type OrganizationPerformance = {
  organization_id: string
  periods: OrganizationPerformancePeriod[]
}

export type OrganizationPerformanceResponse = {
  as_of: string
  items: OrganizationPerformance[]
}

const record = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Invalid organization performance response")
  }

  return value as Record<string, unknown>
}

const integer = (value: unknown): number => {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new Error("Invalid organization performance amount")
  }

  return value
}

const nullableInteger = (value: unknown): number | null => (value === null ? null : integer(value))

const parsePeriod = (value: unknown): OrganizationPerformancePeriod => {
  const row = record(value)

  if (
    ![7, 30, 90].includes(Number(row.days)) ||
    typeof row.planning_complete !== "boolean" ||
    typeof row.credits_complete !== "boolean" ||
    !Array.isArray(row.missing_data) ||
    row.missing_data.some((reason) => typeof reason !== "string")
  )
    throw new Error("Invalid organization performance period")

  const planned = integer(row.planned_work_orders)
  const credits = integer(row.billed_credits)

  if (planned < 0 || credits < 0) throw new Error("Invalid organization performance count")

  return {
    days: integer(row.days) as 7 | 30 | 90,
    planned_work_orders: planned,
    planning_complete: row.planning_complete,
    billed_credits: credits,
    credits_complete: row.credits_complete,
    usage_revenue_cents: integer(row.usage_revenue_cents),
    estimated_base_fee_cents: nullableInteger(row.estimated_base_fee_cents),
    direct_cost_cents: nullableInteger(row.direct_cost_cents),
    cost_breakdown_cents: Object.fromEntries(
      Object.entries(record(row.cost_breakdown_cents)).map(([key, amount]) => [
        key,
        integer(amount),
      ]),
    ),
    estimated_profit_cents: nullableInteger(row.estimated_profit_cents),
    missing_data: row.missing_data as string[],
  }
}

export const queryOrganizationPerformance = async (
  organizationIds: string[],
  options: { signal?: AbortSignal; asOf?: string } = {},
): Promise<OrganizationPerformanceResponse> => {
  const response = record(
    await request<unknown>("/organizations/performance/query", {
      method: "POST",
      signal: options.signal,
      body: JSON.stringify({
        organization_ids: organizationIds,
        ...(options.asOf ? { as_of: options.asOf } : {}),
      }),
    }),
  )

  if (
    typeof response.as_of !== "string" ||
    Number.isNaN(Date.parse(response.as_of)) ||
    !Array.isArray(response.items)
  )
    throw new Error("Invalid organization performance response")

  const expectedIds = new Set(organizationIds)
  const items = response.items.map((value): OrganizationPerformance => {
    const row = record(value)

    if (typeof row.organization_id !== "string" || !expectedIds.delete(row.organization_id)) {
      throw new Error("Unexpected organization performance selection")
    }

    if (!Array.isArray(row.periods)) throw new Error("Missing organization performance periods")

    const periods = row.periods.map(parsePeriod)

    if (periods.length !== 3 || new Set(periods.map((period) => period.days)).size !== 3) {
      throw new Error("Incomplete organization performance periods")
    }

    return { organization_id: row.organization_id, periods }
  })

  if (expectedIds.size) throw new Error("Incomplete organization performance selection")

  if (options.asOf && Date.parse(options.asOf) !== Date.parse(response.as_of)) {
    throw new Error("Inconsistent organization performance timestamp")
  }

  return { as_of: response.as_of, items }
}
